import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Empty, Image, Typography } from "antd";
import { Document, Page } from "react-pdf";
import "../../../../helpers/pdfWorker";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import LoadingIndicator from "../../../Loaders/LoadingIndicator";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";


/** Android / hi-DPI: never render canvas below 2x or PDFs look soft. */
const getPdfDevicePixelRatio = () => {
  if (typeof window === "undefined") return 2;
  const dpr = window.devicePixelRatio || 1;
  return Math.min(Math.max(dpr, 2), 3);
};

const PrevArrow = ({ onClick }) => {
  return (
    <div className="custom-arrow custom-prev" onClick={onClick}>
      <LeftOutlined />
    </div>
  );
};

const NextArrow = ({ onClick }) => {
  return (
    <div className="custom-arrow custom-next" onClick={onClick}>
      <RightOutlined />
    </div>
  );
};

const getInitialIndex = (data, selectedItem) => {
  const index = data?.findIndex(
    (item) =>
      item === selectedItem && (item.type === "image" || item.type === "pdf")
  );
  return index != null && index >= 0 ? index : 0;
};

const PdfSlide = ({
  item,
  index,
  height,
  isMobile,
  numPages,
  loadErrors,
  onDocumentLoadSuccess,
  setLoadErrors,
}) => {
  const wrapRef = useRef(null);
  const [pageWidth, setPageWidth] = useState(() =>
    typeof window !== "undefined"
      ? Math.max(280, Math.floor(window.innerWidth - 48))
      : 320
  );
  const [devicePixelRatio] = useState(getPdfDevicePixelRatio);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const apply = (w) => {
      if (w > 0) setPageWidth(Math.max(240, Math.floor(w)));
    };

    // content box only (excludes padding) so canvas isn’t squeezed/blurred
    apply(el.clientWidth - 16);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      apply(entries[0]?.contentRect?.width ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      className="inv-slider-pdf-container"
      style={{
        overflow: "auto",
        height: height || (isMobile ? "auto" : "800px"),
      }}
    >
      <Document
        file={item.url}
        loading={
          <LoadingIndicator message={"Loading your document, please wait..."} />
        }
        onLoadSuccess={(pdf) => onDocumentLoadSuccess(pdf, index)}
        onLoadError={() => {
          const next = [...loadErrors];
          next[index] = true;
          setLoadErrors(next);
        }}
        error={
          loadErrors[index] ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Empty description="Cannot load pdf file" />
            </div>
          ) : (
            "Failed to load PDF"
          )
        }
      >
        {numPages[index] &&
          Array.from({ length: numPages[index] }, (_, pageIndex) => (
            <Page
              key={`page_${index}_${pageIndex + 1}`}
              pageNumber={pageIndex + 1}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              width={isMobile ? pageWidth : undefined}
              scale={isMobile ? 1 : 1.5}
              devicePixelRatio={devicePixelRatio}
            />
          ))}
      </Document>
    </div>
  );
};

const MobileUploadsView = ({
  data,
  selectedGroup,
  selectedItem,
  height,
  numPages,
  loadErrors,
  onDocumentLoadSuccess,
  setLoadErrors,
}) => {
  const [activeIndex, setActiveIndex] = useState(() =>
    getInitialIndex(data, selectedItem)
  );
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setActiveIndex(getInitialIndex(data, selectedItem));
    setImgError(false);
  }, [data, selectedItem, selectedGroup?.name]);

  useEffect(() => {
    setImgError(false);
  }, [activeIndex]);

  const item = data[activeIndex];
  const canNav = data.length > 1;

  const goPrev = () => {
    setActiveIndex((prev) => (prev <= 0 ? data.length - 1 : prev - 1));
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev >= data.length - 1 ? 0 : prev + 1));
  };

  if (!item) {
    return <Empty description="No files to display" />;
  }

  return (
    <div className="inv-uploads-view inv-uploads-view-mobile">
      <Typography className="inv-slider-filename">{item.path}</Typography>
      <div className="inv-mobile-viewer">
        {canNav ? (
          <button
            type="button"
            className="inv-mobile-nav inv-mobile-nav-prev"
            onClick={goPrev}
            aria-label="Previous"
          >
            <LeftOutlined />
          </button>
        ) : null}

        {item.type === "image" ? (
          <div className="inv-slider-img-container inv-slider-img-container-mobile">
            {!item.url ? (
              <Empty description="Image URL missing" />
            ) : imgError ? (
              <Empty description="Unable to load image" />
            ) : (
              <img
                src={item.url}
                alt={item.path || "Investigation"}
                className="inv-slider-img inv-slider-img-native"
                onError={() => setImgError(true)}
              />
            )}
          </div>
        ) : (
          <PdfSlide
            item={item}
            index={activeIndex}
            height={height}
            isMobile
            numPages={numPages}
            loadErrors={loadErrors}
            onDocumentLoadSuccess={onDocumentLoadSuccess}
            setLoadErrors={setLoadErrors}
          />
        )}

        {canNav ? (
          <button
            type="button"
            className="inv-mobile-nav inv-mobile-nav-next"
            onClick={goNext}
            aria-label="Next"
          >
            <RightOutlined />
          </button>
        ) : null}
      </div>
      {canNav ? (
        <Typography className="inv-mobile-counter">
          {activeIndex + 1} / {data.length}
        </Typography>
      ) : null}
    </div>
  );
};

const UploadsView = ({
  selectedGroup,
  selectedItem,
  width = null,
  height = null,
  isMobile = false,
}) => {
  const data = selectedGroup?.items;
  const [pageNumbers, setPageNumbers] = useState([]);
  const [numPages, setNumPages] = useState([]);
  const [loadErrors, setLoadErrors] = useState([]);

  const initialSlide = getInitialIndex(data, selectedItem);

  const settings = {
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: (data?.length || 0) > 1,
    initialSlide,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const onDocumentLoadSuccess = (pdf, index) => {
    const newNumPages = [...numPages];
    newNumPages[index] = pdf.numPages;
    setNumPages(newNumPages);
    const newPageNumbers = [...pageNumbers];
    newPageNumbers[index] = 1;
    setPageNumbers(newPageNumbers);
  };

  if (!data?.length) {
    return <Empty description="No files to display" />;
  }

  if (isMobile) {
    return (
      <MobileUploadsView
        data={data}
        selectedGroup={selectedGroup}
        selectedItem={selectedItem}
        height={height}
        numPages={numPages}
        loadErrors={loadErrors}
        onDocumentLoadSuccess={onDocumentLoadSuccess}
        setLoadErrors={setLoadErrors}
      />
    );
  }

  return (
    <div
      className="inv-uploads-view"
      style={{
        display: "flex",
        justifyContent: "center",
        height: "800px",
        overflow: "hidden",
      }}
    >
      <Slider
        key={`${selectedGroup?.name}-${initialSlide}`}
        {...settings}
        style={{ height: "500px", width: "1000px" }}
      >
        {data.map((item, index) => (
          <div key={index}>
            <Typography className="inv-slider-filename">{item.path}</Typography>
            {item.type === "image" ? (
              <div className="inv-slider-img-container">
                <Image
                  src={item.url}
                  className="inv-slider-img"
                  width="600px"
                  preview={{
                    getContainer: () => document.body,
                  }}
                />
              </div>
            ) : (
              <PdfSlide
                item={item}
                index={index}
                height={height}
                isMobile={false}
                numPages={numPages}
                loadErrors={loadErrors}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                setLoadErrors={setLoadErrors}
              />
            )}
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default UploadsView;
